// Slots, layouts and the small decisions of the interface that need no page (WS-49 track T3). Pure functions.
/** @typedef {import('../settings/typedefs.js').LayoutDef} LayoutDef */

/** The layout a preset asks for (`layout.id`): an engine layout, one the package supplies, else the default. */
export const layoutOf = (registry, id) => registry.layouts[id] || registry.layouts.default;

/**
 * What is drawn where: per slot of the layout, its control ids that are shown and that the interface can draw
 * (`can`: a component exists and its data is there), in the layout's order, each control once.
 * @param {LayoutDef} layout  @param {Iterable<string>} shown  @param {(id: string) => boolean} [can]
 * @returns {{ [slot: string]: string[] }}
 */
export function plan(layout, shown, can = () => true) {
  const on = new Set(shown), seen = new Set(), out = {};
  for (const [slot, ids] of Object.entries(layout?.slots || {})) {
    out[slot] = (Array.isArray(ids) ? ids : []).filter((id) => on.has(id) && !seen.has(id) && can(id) && !!seen.add(id));
  }
  return out;
}

const MODES = ['off', 'icons', 'text', 'both'];
/** `labels.<category>` of the resolved values; a category nobody set, or an unknown value, shows icon and name. */
export const labelMode = (values, category) => (MODES.includes(values?.[`labels.${category}`]) ? values[`labels.${category}`] : 'both');
/** What a place label draws in a mode. */
export const labelParts = (mode) => ({ shown: mode !== 'off', icon: mode === 'icons' || mode === 'both', text: mode === 'text' || mode === 'both' });

/** `"MM-DD"` (the `time.date` value) from a month and a day, and back (`"6-21"` is read too). */
export const monthDay = (m, d) => `${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
export const parseMonthDay = (s) => { const [m, d] = String(s || '').split('-').map(Number); return m >= 1 && m <= 12 && d >= 1 && d <= 31 ? [m, d] : null; };

// ---- texts that used to be cut out of one content sentence (contract section 6). Each now has a field or a key of
// its own (README.md "Content fields"); the cut is kept only as the fallback for content that has no such field yet,
// so today's package reads exactly as before.
/** A unit type's guest count. `kind`: '' = the full phrase, 'short' = the list row, 'stat' = the card's figure. */
export function guestsText(unit, kind, i18n) {
  const cap = unit?.capacity;
  if (cap && Number.isFinite(cap.ideal) && Number.isFinite(cap.max)) return i18n.T(`unit.guests${kind ? `.${kind}` : ''}`, { ideal: i18n.num(cap.ideal), max: i18n.num(cap.max) });
  const s = i18n.tx(unit?.guests);
  return kind === 'short' ? s.replace('Ideal for ', '') : kind === 'stat' ? s.replace('Ideal for ', 'Ideal ') : s; // legacy content
}
/** The name on a category tab: `short`, else (legacy content) the first word of the label. */
export const categoryTab = (cat, i18n) => i18n.tx(cat.short) || i18n.tx(cat.label).split(' ')[0];
/** The line under a place in the list: `teaser`, else (legacy content) the first sentence of the text. */
export const teaserText = (place, i18n) => i18n.tx(place.teaser) || i18n.tx(place.blurb).split('.')[0];
